<template>
  <div
    class="module-placeholder"
    :class="{
      'is-dragover': isDragOver,
      'is-full': isModuleFull,
    }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div class="module-header">
      <slot name="header">
        <span class="module-title">{{ title }} {{ index + 1 }}</span>
      </slot>

      <div class="module-actions">
        <span class="items-count" v-if="topics.length === maxItems">
          {{ topics.length }}/{{ maxItems }}
        </span>

        <b-button
          v-if="canEdit"
          type="is-ghost"
          icon-left="edit"
          size="is-small"
          @click="$emit('edit', index)"
        />

        <b-button
          v-if="canRemove && topics.length === 0"
          type="is-ghost"
          icon-left="trash"
          size="is-small"
          @click="$emit('clear', index)"
        />
      </div>
    </div>

    <div class="module-content">
      <slot name="content" />

      <template v-if="topics.length === 0">
        <div class="empty-state">
          Drop topics here for {{ title }} {{ index + 1 }}
        </div>
      </template>
      <template v-else>
        <CompactTopicCard
          v-for="topic in topics"
          :key="topic.id"
          :topic="topic"
          :usage="topic.usage"
          :canRemove="canRemove"
          @remove="$emit('remove-topic', topic.id)"
          @click="$emit('select-topic', topic.id)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import CompactTopicCard from "../explorer/molecules/CompactTopicCard.vue";

export default {
  name: "ModulePlaceholder",

  components: { CompactTopicCard },

  props: {
    index: { type: Number, required: true },
    topics: { type: Array, default: () => [] },
    maxItems: { type: Number, default: 0 }, // 0 means unlimited
    title: { type: String, default: "Module" },
    canEdit: { type: Boolean, default: false },
    canRemove: { type: Boolean, default: false },
  },

  data() {
    return {
      isDragOver: false,
    };
  },

  computed: {
    isModuleFull() {
      return this.maxItems > 0 && this.topics.length >= this.maxItems;
    },

    topicIds() {
      return this.topics.map((topic) => topic.id);
    },
  },

  methods: {
    handleDragOver() {
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    handleDrop(event) {
      this.isDragOver = false;

      try {
        const topicData = JSON.parse(event.dataTransfer.getData("text/plain"));

        if (this.isModuleFull) {
          this.$emit("full", { topicId: topicData.id, index: this.index });
          return;
        }

        // Check if topic already exists in this module
        if (this.topicIds.includes(topicData.id)) {
          this.$buefy.toast.open({
            message: `This topic is already in this ${this.title}`,
            type: "is-warning",
          });
          return;
        }

        this.$emit("drop", { topicId: topicData.id, index: this.index });
      } catch (error) {
        console.error("Drop handling error:", error);
      }
    },
  },
};
</script>

<style scoped>
.module-placeholder {
  border: 1px dashed #dbdbdb;
  border-radius: 4px;
  padding: 0.5rem;
  min-height: 60px;
  transition: all 0.3s ease;
}

.module-placeholder.is-dragover {
  border-color: #00d1b2;
  background-color: rgba(0, 209, 178, 0.1);
}

.module-placeholder.is-full {
  border-color: #dbdbdb;
  background-color: rgba(219, 219, 219, 0.1);
}

.module-placeholder.is-full.is-dragover {
  border-color: #ff3860;
  background-color: rgba(255, 56, 96, 0.1);
}

.items-count {
  font-size: 0.8rem;
  color: #7a7a7a;
  text-align: right;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.module-title {
  font-weight: 500;
  color: #363636;
}

.module-actions {
  display: flex;
  align-items: center;
}

.module-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.empty-state {
  color: #7a7a7a;
  text-align: center;
  font-size: 0.9rem;
}
</style>
